import { generateName } from '@common/Common';
import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue';
import { guid } from '@common/Guid';
import Navigation from '@common/Navigation.vue';
import { ICommandBase } from '@Quest/cqrs';
import ProjectNavigation from '@Quest/Projects/Components/ProjectNavigation.vue';
import { ProjectPlanEditorFormSubmitEventPayload, ProjectPlanEditorViewModel } from '@Quest/Projects/Components/ProjectPlanEditor';
import ProjectPlanEditor from "@Quest/Projects/Components/ProjectPlanEditor.vue";
import { CloneProjectPlanCommandV1, CreateProjectPlanCommandV1, DeleteProjectPlanCommandV1, ExportProjectPlanCommandV1, SubmitProjectPlanCommandV1, UnsubmitProjectPlanCommandV1, UpdateProjectActivePlanCommandV1, UpdateProjectPlanCommandV1 } from '@Quest/Projects/Domain/commands-core';
import ProjectsCommands from "@Quest/Projects/Domain/commands-vue-component";
import ProjectsQueries from "@Quest/Projects/Domain/queries-vue-component";
import { ProjectPageReadModel, ProjectPlanReadModel, ProjectPlanTemplateReadModel } from '@Quest/Projects/Domain/readmodels';
import moment from "moment";
import Component, { mixins } from 'vue-class-component';

@Component({ components: { PortfolioNavigation, ProjectNavigation, Navigation, ProjectPlanEditor } })
export default class ProjectPage extends mixins(ProjectsQueries, ProjectsCommands)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get tchProjectId() { return Number.parseInt(this.$route.params.tchProjectId); }
    get projectId() { return this.$route.params.projectId; }

    rm: ProjectPageReadModel | null = null;
    projectPlanEditor = new ProjectPlanEditorViewModel();

    get isWorkspaceMember(): boolean { return this.rm?.IsWorkspaceMember ?? false }
    get isProjectOwner(): boolean { return this.rm?.IsProjectOwner ?? false }
    get activePlan(): ProjectPlanReadModel | null { return this.rm!.Plans.find(a => a.IsActive) ?? null; }
    get backupPlans(): ProjectPlanReadModel[] { return this.rm!.Plans.filter(a => !a.IsActive); }
    get planTemplates() { return this.rm!.PlanTemplates; }


    getFormattedDateStr(date: string | null): string | null
    {
        if (!date) return null;
        return moment(date).format('MMM D, YYYY');
    }

    getFormattedTimeStr(date: string | null): string | null
    {
        if (!date) return null;
        return moment(date).format('LT');
    }


    showPlanCreator(template: ProjectPlanTemplateReadModel | null)
    {
        this.projectPlanEditor.showCreator(template);
    }

    showPlanEditor(plan: ProjectPlanReadModel)
    {
        this.projectPlanEditor.showEditor(plan);
    }


    handleUpdateProjectActivePlanCommand(plan: ProjectPlanReadModel)
    {
        this.invokeUpdateProjectActivePlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, this.rm.ActivePlanId, plan.Id);
    }

    handleExportProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        this.invokeExportProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, this.rm.ActivePlanId, plan.Id);
    }

    handleProjectPlanEditorFormSubmit(e: ProjectPlanEditorFormSubmitEventPayload)
    {
        if (e.id == null)
        {
            this.invokeCreateProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, this.rm.ActivePlanId, guid(), e.name, e.duration, e.granularity, e.denomination, e.planningHorizonInYears, e.submissionDueDate);
        }
        else
        {
            this.invokeUpdateProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, this.rm.ActivePlanId, e.id, e.name, e.duration, e.granularity, e.denomination, e.planningHorizonInYears, e.submissionDueDate);
        }
    }

    handleSubmitProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (confirm(`Are you sure you want to submit the plan: ${plan.Name}`))
        {
            this.invokeSubmitProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, plan.Id);
        }
    }

    handleUnsubmitProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (confirm(`Are you sure you want unsubmit the plan: ${plan.Name}`))
        {
            this.invokeUnsubmitProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, plan.Id);
        }
    }

    handleDeleteProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (confirm(`Are you sure you want to delete: ${plan.Name}`))
        {
            this.invokeDeleteProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, this.rm.ActivePlanId, plan.Id);
        }
    }

    handleCloneProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        this.invokeCloneProjectPlanCommandV1(this.workspaceId, this.rm.PortfolioId, this.projectId, plan.Id, guid(), generateName(plan.Name));
    }


    mounted()
    {
        document.title = "Project  | TransparentChoice";
        this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "ProjectPageReadModel")
        {
            this.rm = model as ProjectPageReadModel;

            if (this.rm.IsPlanningEnabled === false)
            {
                location.href = `/#/portfolio/planning/${this.workspaceId}/${this.tchProjectId}`;
            }
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (CreateProjectPlanCommandV1.matchType(cmd))
        {
            this.projectPlanEditor.close();
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }
        else if (CloneProjectPlanCommandV1.matchType(cmd))
        {
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }
        else if (UpdateProjectPlanCommandV1.matchType(cmd))
        {
            this.projectPlanEditor.close();
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }
        else if (SubmitProjectPlanCommandV1.matchType(cmd))
        {
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }
        else if (UnsubmitProjectPlanCommandV1.matchType(cmd))
        {
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }

        else if (DeleteProjectPlanCommandV1.matchType(cmd))
        {
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }
        else if (UpdateProjectActivePlanCommandV1.matchType(cmd))
        {
            this.runProjectPlansQueryV1(this.workspaceId, this.tchProjectId, this.projectId);
        }
        else if (ExportProjectPlanCommandV1.matchType(cmd)) 
        {
            if (result) 
            {
                location.href = result;
            }
        }
    }
}